import type { AgentDaemon, AgentServiceStatus } from '@/lib/types';

/**
 * Match a persisted daemon record to its live on-box service entry. The
 * daemon notify doesn't carry the on-box service id, so this reuses the same
 * identity the server's daemon upsert does: the port when one was registered,
 * falling back to the display name (which `reflex-service register` writes as
 * the service definition's label). Returns `undefined` when the statuses
 * haven't loaded or the service is no longer on the box.
 */
export function matchDaemonService(
  daemon: AgentDaemon,
  services: AgentServiceStatus[] | undefined,
): AgentServiceStatus | undefined {
  if (!services) return undefined;
  if (daemon.port != null) {
    const byPort = services.find((s) => s.port === daemon.port);
    if (byPort) return byPort;
  }
  return services.find((s) => s.label === daemon.name || s.id === daemon.name);
}

export type DaemonLiveState = 'running' | 'stopped' | 'unknown';

/**
 * Fold a live service probe into the three states the server list renders.
 * Without probe data (statuses not passed / not yet loaded) the list keeps
 * its historical optimistic green. With probe data, the pid liveness answer
 * wins: a dead pid or an `error:*` status is `stopped`; a daemon the probe
 * can't see at all (box unreachable, service removed) is `unknown`.
 */
export function daemonLiveState(
  service: AgentServiceStatus | undefined,
  probed: boolean,
): DaemonLiveState {
  if (!probed) return 'running';
  if (!service) return 'unknown';
  if (service.running === false || service.status.startsWith('error:')) return 'stopped';
  if (service.running === true || service.status === 'ready') return 'running';
  return 'unknown';
}
